// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/useAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mapServerMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mergeDisplayMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import {
  type AskPunttChat,
  type AskPunttMessage,
  type GetAskPunttChatHistoryResponse,
} from "@mg/schemas/src/christo/askPuntt";
import { type Query } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  applyStreamSnapshot,
  streamMessageIdFor,
  streamMessageIdForThread,
} from "./applyStreamSnapshot";
import {
  createAskV2Transport,
  type AskV2Transport,
} from "./createAskV2Transport";
import {
  HISTORY_POLL_SOCKET_MS,
  historyPayloadCoversLocalTurn,
  mapServerMessages,
  nextAskV2HistoryPollMs,
} from "./mapServerMessages";
import { mergeDisplayMessages } from "./mergeDisplayMessages";
import { turnUserIdFromStreamMessageId } from "./runningTurn";
import { type AskV2TransportSnapshot } from "./types";

import {
  PUSH_STALL_MS,
  type StreamingState,
  useAskPunttStream,
} from "../../hooks/useAskPunttStream";
import { useAskPunttChatHistory } from "../../utils/queries/askPuntt";
import { generateUUID } from "../../utils/uuid";
import {
  abortAskV2Message,
  createAskV2Chat,
  regenerateAskV2Message,
  sendAskV2Message,
} from "../services/chat";

export type UseAskV2TransportOptions = {
  chatId?: string | null;
  enabled?: boolean;
  presentation?: "chat" | "feed";
};

export type AskV2TransportApi = AskV2TransportSnapshot & {
  setComposer: AskV2Transport["setComposer"];
  setWebSearch: AskV2Transport["setWebSearch"];
  addQuotedContext: AskV2Transport["addQuotedContext"];
  removeQuotedContext: AskV2Transport["removeQuotedContext"];
  clearQuotedContexts: AskV2Transport["clearQuotedContexts"];
  setCommentContext: AskV2Transport["setCommentContext"];
  removeCommentContext: AskV2Transport["removeCommentContext"];
  clearCommentContexts: AskV2Transport["clearCommentContexts"];
  send: AskV2Transport["send"];
  abort: AskV2Transport["abort"];
  retry: AskV2Transport["retry"];
  beginEdit: AskV2Transport["beginEdit"];
  setEditDraft: AskV2Transport["setEditDraft"];
  cancelEdit: AskV2Transport["cancelEdit"];
  submitEdit: AskV2Transport["submitEdit"];
  historyLoading: boolean;
  historyError: boolean;
  chatTitle?: string;
  historyChat?: AskPunttChat;
  historyMessages: AskPunttMessage[];
  displayMessages: AskPunttMessage[];
  /**
   * The live AskPuntt snapshot the running turn is publishing, or null. It is
   * already APPLIED to `historyMessages`/`displayMessages`; this is the raw
   * payload, so a reader that needs a field the thread does not carry (the
   * provisional `verifiedFindings`) does not have to open a second socket for
   * it.
   */
  streamSnapshot: StreamingState | null;
  /** The snapshot's message id (`w<userMessageId>`), or null when none is pending. */
  streamMessageId: string | null;
  /** `review:<id>` and `canvas:<id>` keys the viewer has not opened. */
  unseenArtifactKeys: ReadonlySet<string>;
  /**
   * The message the armed address belongs to, or null when none is armed.
   *
   * The address is `w<userMessageId>`, so this is the same fact read rather
   * than a second derivation -- and it is what lets a reader that holds the
   * history thread decide whether the turn the socket is for has reached that
   * thread yet; see `useAskV2PlanProgress`.
   */
  streamTurnUserId: string | null;
};

export function useAskV2Transport(
  options?: UseAskV2TransportOptions,
): AskV2TransportApi {
  const routeChatId = options?.chatId ?? null;
  const presentation = options?.presentation ?? "chat";

  const transportRef = useRef<AskV2Transport | null>(null);
  if (transportRef.current == null) {
    transportRef.current = createAskV2Transport({
      id: generateUUID,
      initialChatId: routeChatId,
      api: {
        createChat: ({
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          createAskV2Chat(
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        sendMessage: ({
          chatId: activeChatId,
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          sendAskV2Message(
            activeChatId,
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        abortMessage: ({ chatId: activeChatId, messageId }) =>
          abortAskV2Message(activeChatId, messageId),
        regenerateMessage: ({ chatId: activeChatId, messageId, content }) =>
          regenerateAskV2Message(activeChatId, messageId, content),
      },
    });
  }

  const transport = transportRef.current;
  const [snapshot, setSnapshot] = useState(transport.getSnapshot);
  const lastSyncRef = useRef<{ chatId: string | null; updatedAt: number }>({
    chatId: null,
    updatedAt: 0,
  });
  const pollStartedAtRef = useRef<number | null>(null);
  // Whether the server has said, at any point in THIS poll, that the turn is
  // still running. A later read that could not ask must not undo that and stop
  // the poll at the cap; see `nextAskV2HistoryPollMs`.
  const pendingTurnSeenRef = useRef(false);
  const wasInFlightRef = useRef(false);
  // Whether the socket is currently CARRYING this turn — connected, and a
  // `stream_update` inside `PUSH_STALL_MS`. Read through a ref because the
  // stream hook is subscribed from the snapshot this one reads: the history
  // query has to be declared first, so the flag cannot be a render value here.
  const socketLiveRef = useRef(false);
  // The `errorUpdateCount` this query carried at its last NON-error state.
  //
  // The retry backoff is sized by CONSECUTIVE failed reads, and neither
  // counter on query state is that on its own:
  //
  // * `fetchFailureCount` is reset to 0 at the START of every fetch, and the
  //   history query runs with `retry: false` (`utils/queries/askPuntt`), so
  //   exactly one increment lands per failed fetch. It reads 1 on every error
  //   however long the backend has been down, which pins the interval at the
  //   2s base and makes `HISTORY_POLL_ERROR_MAX_MS` unreachable — a 30 rpm
  //   hammer on an endpoint that is already failing.
  // * `errorUpdateCount` does survive the fetch dispatch, but it is monotonic
  //   over the whole life of the query, so a fresh blip after an hour of
  //   health would open at the 15s cap instead of recovering in seconds.
  //
  // The distance between the two is what "consecutive" means: rebase on a real
  // SUCCESS, and the difference is the run of failures since the last good
  // read.
  //
  // Only a success — NOT "anything that is not an error". `refetchInterval` is
  // re-run on every query dispatch, not once per settled fetch, and the `fetch`
  // dispatch that opens each retry rewrites a query with no data yet to
  // `status: "pending"` (query-core's `fetchState` spreads `{ error: null,
  // status: "pending" }` whenever `data === undefined`). So a chat whose history
  // has NEVER loaded — the frozen empty thread this poll exists for — is handed
  // `pending -> error -> pending -> error`, never two errors in a row. Rebasing
  // on not-error zeroed the run on every one of those `pending` steps and pinned
  // the retry at the 2s base forever, with the cap unreachable.
  const historyErrorBaselineRef = useRef(0);

  const chatId = routeChatId ?? snapshot.chatId;
  const enabled = options?.enabled !== false && !!chatId;

  useEffect(() => {
    if (!routeChatId) return;
    if (transport.getSnapshot().chatId === routeChatId) return;
    // why: project routes learn sharedChatId after load — bind before history
    // returns so send() posts into the shared chat instead of createChat().
    transport.hydrateFromServer({ chatId: routeChatId, messages: [] });
    lastSyncRef.current = { chatId: null, updatedAt: 0 };
    pollStartedAtRef.current = null;
    pendingTurnSeenRef.current = false;
    historyErrorBaselineRef.current = 0;
  }, [routeChatId, transport]);

  const historyQuery = useAskPunttChatHistory(chatId, {
    enabled,
    refetchInterval: (query: Query<GetAskPunttChatHistoryResponse, Error>) => {
      const snapshot = transport.getSnapshot();
      const inFlight = snapshot.inFlight;
      if (inFlight && !wasInFlightRef.current) {
        pollStartedAtRef.current = null;
        pendingTurnSeenRef.current = false;
      }
      wasInFlightRef.current = inFlight;
      // `pendingTurn` is the server's word about the transcript it RETURNED. A
      // payload the person's own send has not reached yet is the previous
      // turn's, and its `false` must not be read as "no turn will run" — that
      // cleared the interval and the query never refetched again, which is the
      // frozen originating tab (see `historyPayloadCoversLocalTurn`).
      const coversLocalTurn = historyPayloadCoversLocalTurn(
        query.state.data?.messages,
        snapshot.messages,
      );
      // Rebasing is an ASSIGNMENT of a value already sitting on query state,
      // never an increment, which is what makes it safe from here:
      // `refetchInterval` is evaluated more than once per fetch, and running
      // this twice on the same state yields the same answer. See
      // `historyErrorBaselineRef` for why neither counter works raw, and why
      // only a `success` is allowed to end a run of failures — a `pending`
      // fetch dispatch between two failures must not zero the count.
      if (query.state.status === "success") {
        historyErrorBaselineRef.current = query.state.errorUpdateCount;
      }
      const consecutiveErrors =
        query.state.errorUpdateCount - historyErrorBaselineRef.current;
      const next = nextAskV2HistoryPollMs({
        queryStatus: query.state.status,
        messages: query.state.data?.messages ?? [],
        inFlight,
        pollStartedAt: pollStartedAtRef.current,
        now: Date.now(),
        agent: query.state.data?.chat?.agent,
        // Why the poll keeps going past the warhol cap; see the function.
        // `undefined` here means the server did not say, which is the case for
        // every chat but warhol and for a read that could not ask.
        pendingTurn: coversLocalTurn
          ? query.state.data?.pendingTurn
          : undefined,
        pendingTurnSeen: pendingTurnSeenRef.current,
        // Both derived from query state, never incremented here:
        // `refetchInterval` is evaluated more than once per fetch, so a
        // counter of our own would advance on a re-render and back off a
        // healthy poll.
        errorCount: consecutiveErrors,
        hasPayload: query.state.data != null,
      });
      pollStartedAtRef.current = next.pollStartedAt;
      pendingTurnSeenRef.current = next.pendingTurnSeen;
      // The socket is carrying this turn, so the poll drops to a safety net:
      // 20s instead of 2s. Only ever SLOWER — this can make the poll wait
      // longer, never bring it back early — so every reason
      // `nextAskV2HistoryPollMs` has to stop (`false`) or to keep the fast
      // cadence crosses over unchanged.
      //
      // NOT while the last read FAILED. `next.interval` is then the error
      // retry, which starts at 2s precisely so a blip recovers in seconds;
      // relaxing it onto the socket would make every failed read take 20s to
      // be noticed, and the socket cannot deliver the settled answer anyway —
      // it never carries it. "Only ever slower" is about the healthy cadence.
      const relaxing = socketLiveRef.current && query.state.status !== "error";
      return relaxing && next.interval !== false
        ? Math.max(next.interval, HISTORY_POLL_SOCKET_MS)
        : next.interval;
    },
  });

  /**
   * The snapshot a running warhol review publishes to, or null.
   *
   * Only warhol writes one under `w<userMessageId>`, and only while it is
   * reviewing: the tail being the person's own message IS "the answer has not
   * landed". Everything else — rembrandt, a finished review, a non-review chat
   * — subscribes to nothing and pays nothing.
   */
  const rawServerMessages = historyQuery.data?.messages;
  const historyAgent = historyQuery.data?.chat?.agent;
  /**
   * The same address read off the thread this tab already holds.
   *
   * The socket must not wait for a history read. A read that is failing — the
   * state the review runs in for a whole turn if one GET times out — used to
   * leave `streamMessageId` null, so `useAskPunttStream` never armed, the
   * socket closed seconds after Start review, and the thread, rail and findings
   * all sat still while the turn ran server-side.
   */
  const localStreamMessageId = useMemo(
    () => streamMessageIdForThread(snapshot.messages),
    [snapshot.messages],
  );
  const streamMessageId = useMemo(() => {
    // A chat the server has already named as something other than warhol writes
    // no snapshot under this address, so there is nothing to subscribe to —
    // and absent means rembrandt, which is every chat created before warhol
    // existed (`askPunttChatSchema.agent`).
    //
    // NO PAYLOAD is the case worth the attempt, and it is the state a failing
    // read leaves behind: nothing has ever named the agent, so the only thing
    // that can name the turn is the thread in this tab — and every chat this UI
    // starts is a warhol one (`createAskV2Chat` posts `agent: "warhol"`).
    //
    // The trade that buys, made deliberately: a chat the server WOULD name as
    // not warhol also passes here while no payload has ever been read, so a
    // send into one — failing read, non-warhol chat, send inside that window —
    // arms a socket nothing publishes under, plus its `/stream` poll.
    // `AskV2ChatPage` takes its chatId from the route, so ask-v2 can host a
    // pre-existing rembrandt chat. Narrow, bounded, and self-correcting on the
    // first payload that arrives; the alternative is a review that runs dark,
    // which is the bug this whole change removes. The only thing that could
    // name the agent here without a payload is the chat record or the send ACK,
    // and neither carries it.
    if (historyQuery.data != null && historyAgent !== "warhol") return null;
    const fromHistory = streamMessageIdFor(rawServerMessages);
    // A payload that does not yet carry the local turn is the PREVIOUS turn's,
    // and its answer is its tail — so it names no running turn. `historyPayloadCoversLocalTurn`
    // is the same test the poll runs on, and it is read here for the same
    // reason: the payload the person's own send has not reached must not speak
    // for the turn that send started.
    //
    // When it DOES cover the local turn, history is the authority and its
    // `null` is an answer: the turn is over, and the local tail may simply not
    // have caught up with the settled row yet.
    const historyIsCurrent = historyPayloadCoversLocalTurn(
      rawServerMessages,
      snapshot.messages,
    );
    return historyIsCurrent
      ? fromHistory
      : (localStreamMessageId ?? fromHistory);
  }, [
    historyAgent,
    localStreamMessageId,
    rawServerMessages,
    snapshot.messages,
  ]);

  // The same hook the v1 chat streams on, untouched: one shared socket per tab,
  // a `w`-prefixed key warhol writes, and its own `/stream` poll fallback for a
  // dead socket — which is what keeps the rows moving when the socket is
  // blocked. `expectPushes` is true for the whole life of the subscription
  // because the turn is generating for exactly that long.
  const {
    streamingState: streamSnapshot,
    wsConnected,
    lastWsPushAt,
  } = useAskPunttStream(chatId ?? "", streamMessageId, {
    enabled: !!chatId && !!streamMessageId,
    expectPushes: true,
  });

  /**
   * Whether the socket is carrying THIS turn, and so whether the history poll
   * can fall back to its safety cadence.
   *
   * Two conditions, because one is not enough:
   *
   * * `wsConnected` — the socket is open and answering heartbeats. The hook
   *   already folds the turn-scoped push-stall guard in (`expectPushes` plus
   *   `PUSH_STALL_MS` of silence), so a socket that is open but no longer
   *   delivering reads as down.
   * * a `stream_update` inside that same window. A turn that has not published
   *   its first frame yet is `connected` — the socket is fine, the snapshot is
   *   simply not there — and relaxing the poll then would leave the thread with
   *   NO source at all for the first seconds of every turn, which is a
   *   regression dressed as an optimisation.
   *
   * And it is switched off the moment the snapshot says the turn is over.
   * `content` is the SETTLED answer and the socket never carries it (warhol
   * leaves it empty; christo writes it from the mirror), so the poll is the
   * only thing that can deliver the answer once the turn has ended — waiting
   * 20s for it would trade the one thing the thread exists for to save a poll.
   */
  socketLiveRef.current =
    wsConnected &&
    lastWsPushAt != null &&
    Date.now() - lastWsPushAt <= PUSH_STALL_MS &&
    streamSnapshot?.status !== "completed" &&
    streamSnapshot?.status !== "failed";

  /**
   * History with the live snapshot laid on the pending turn's row.
   *
   * Everything downstream reads THIS and not the raw response. Live findings,
   * answer text, and fan-out chips hang off the overlaid message; Progress still
   * reads only its `write_plan` checklist from `agentSteps`. The history poll
   * remains the source of truth for the settled answer and persisted rows.
   *
   * The list handed to the overlay has to be one the ARMED ADDRESS can be found
   * in. `applyStreamSnapshot` resolves its target row from the rows it is given
   * — the tail assistant row, or a row appended after a tail user one — while
   * the subscription may have been armed from the LOCAL thread instead
   * (`localStreamMessageId`). Handed the pre-send payload in that state it takes
   * that payload's tail, which is the PREVIOUS turn's settled answer, and merges
   * this turn's `streamingContent`/`agentSteps` into it: a finished answer
   * starts rewriting itself as the new turn streams.
   *
   * So the payload is overlaid only when it names the same address the socket is
   * subscribed to. When it does not, the local tail is merged in first — the
   * very merge the thread already renders, so no row is invented here — which
   * puts the running turn's own message last and makes the overlay APPEND its
   * row under that address, exactly the shape history itself takes once the read
   * recovers. If even that cannot name the address, the payload is returned
   * untouched: no live field belongs on a row this turn does not own.
   */
  const serverMessages = useMemo(() => {
    const base = rawServerMessages ?? [];
    if (streamMessageIdFor(base) === streamMessageId) {
      return applyStreamSnapshot(base, streamSnapshot);
    }
    const withLocalTurn = mergeDisplayMessages({
      historyMessages: base,
      transportMessages: snapshot.messages,
      chatId: chatId ?? "",
    });
    return streamMessageIdFor(withLocalTurn) === streamMessageId
      ? applyStreamSnapshot(withLocalTurn, streamSnapshot)
      : base;
  }, [
    chatId,
    rawServerMessages,
    snapshot.messages,
    streamMessageId,
    streamSnapshot,
  ]);

  useEffect(() => transport.subscribe(setSnapshot), [transport]);

  useEffect(() => {
    if (!enabled || !chatId) {
      if (lastSyncRef.current.chatId) {
        transport.reset();
        lastSyncRef.current = { chatId: null, updatedAt: 0 };
      }
      return;
    }

    if (!historyQuery.data) return;

    // why: getChatHistory returns a bare cast with no validation, and V1 guards
    // this exact field (`if (!chatData?.messages) return` in ask-puntt/chat.tsx)
    // — a response without `messages` makes mapServerMessages' `.filter` throw
    // inside this effect and unmounts the Ask V2 chat via the error boundary.
    if (!historyQuery.data.messages) return;

    // `serverMessages` is the snapshot-overlaid list, and it is an argument to
    // this effect below: a `stream_update` must re-map the thread, or the live
    // agent steps would reach the rail but never the thread's own rows.
    const mapped = mapServerMessages(serverMessages, presentation);
    if (lastSyncRef.current.chatId !== chatId) {
      // why: hydrateFromServer replaces the thread wholesale. That is right for
      // a chat the transport is not bound to yet, but wrong for the FIRST
      // payload of the chat it is already on: the composer renders while
      // history is still loading, so a turn sent in that window is an
      // optimistic sending/failed row the server has not echoed back yet. A
      // blind hydrate drops it — the bubble vanishes mid-send, the ACK can no
      // longer find its clientId to mark sent, and a failed send loses its
      // retry affordance. syncFromServer keeps that local tail.
      if (transport.getSnapshot().chatId === chatId) {
        transport.syncFromServer(mapped);
      } else {
        transport.hydrateFromServer({ chatId, messages: mapped });
      }
    } else if (historyQuery.dataUpdatedAt !== lastSyncRef.current.updatedAt) {
      transport.syncFromServer(mapped);
    }

    lastSyncRef.current = {
      chatId,
      updatedAt: historyQuery.dataUpdatedAt,
    };
  }, [
    chatId,
    enabled,
    historyQuery.data,
    historyQuery.dataUpdatedAt,
    presentation,
    serverMessages,
    transport,
  ]);

  const displayMessages = useMemo(
    () =>
      mergeDisplayMessages({
        historyMessages: serverMessages,
        transportMessages: snapshot.messages,
        chatId: chatId ?? "",
      }),
    [chatId, serverMessages, snapshot.messages],
  );
  const unseenArtifactKeys = useMemo(
    () => new Set(historyQuery.data?.userSeen?.unseenArtifactKeys ?? []),
    [historyQuery.data?.userSeen?.unseenArtifactKeys],
  );

  return {
    ...snapshot,
    setComposer: transport.setComposer,
    setWebSearch: transport.setWebSearch,
    addQuotedContext: transport.addQuotedContext,
    removeQuotedContext: transport.removeQuotedContext,
    clearQuotedContexts: transport.clearQuotedContexts,
    setCommentContext: transport.setCommentContext,
    removeCommentContext: transport.removeCommentContext,
    clearCommentContexts: transport.clearCommentContexts,
    send: transport.send,
    abort: transport.abort,
    retry: transport.retry,
    beginEdit: transport.beginEdit,
    setEditDraft: transport.setEditDraft,
    cancelEdit: transport.cancelEdit,
    submitEdit: transport.submitEdit,
    historyLoading: enabled && historyQuery.isLoading,
    historyError: enabled && historyQuery.isError,
    chatTitle: historyQuery.data?.chat?.title,
    historyChat: historyQuery.data?.chat,
    historyMessages: serverMessages,
    displayMessages,
    streamSnapshot: streamSnapshot ?? null,
    streamMessageId,
    unseenArtifactKeys,
    streamTurnUserId: turnUserIdFromStreamMessageId(streamMessageId),
    /**
     * A turn is running if the transport says so, OR history says a turn is
     * still running, OR the socket is armed for one.
     *
     * `transport.inFlight` is what the composer's Stop button renders on, and the
     * transport DERIVES it from the message list (`deriveInFlight`): any row
     * `streaming`, else "the tail is the person's own message". A mid-turn
     * payload can read as finished under that rule — a warhol turn writes its
     * plan and a prose question onto the turn's row minutes before the turn ends,
     * so a sync that lands that row as the tail has no streaming row and no user
     * tail. On the Story 4 e2e the Stop button vanished for a tick, 50s into a
     * 13-minute review, on exactly such a read (`run-4/analysis.json` →
     * `stopReappeared: 1`).
     *
     * `pendingTurn` is the SERVER's word about the transcript it returned — the
     * streaming snapshot's own `pending_turn`, which is true for the whole of a
     * warhol turn — so it is the one answer here that is not a guess about shape.
     * It is read exactly as `refetchInterval` reads it: only for a payload that
     * covers the local turn, because straight after a send the newest payload is
     * the PREVIOUS turn's and its `false` is about a turn that is over
     * (`historyPayloadCoversLocalTurn`).
     *
     * And the armed address, which names the running turn and holds by
     * construction. All three are FLOORS on "a turn is running": not one of them
     * can make a settled turn look live, because a settled turn clears all three
     * — the address when history returns the settled answer, and `pendingTurn`
     * when the snapshot stops saying the turn is pending.
     */
    inFlight:
      snapshot.inFlight ||
      (historyQuery.data?.pendingTurn === true &&
        historyPayloadCoversLocalTurn(
          historyQuery.data?.messages,
          snapshot.messages,
        )) ||
      streamMessageId != null,
  };
}
